import { assert, assertNonNullable, requireNonNullable } from '@disy/lib/custom-error';

import type { Authority } from 'cadenza/api-client/srs-api';
import type { MapViewLayout } from 'cadenza/model/map-view-layout.gen';
import type { RootStateOr } from 'cadenza/store/cadenza-store';
import type { LayerId } from 'cadenza/workbook/api/layer-id';
import type { ViewDto } from 'cadenza/workbook/api/view-dto';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import type { WorkbookLayerDto } from 'cadenza/workbook/api/workbook-layer-dto';
import type { WorkbookLayerTreeNode } from 'cadenza/workbook/api/workbook-layer-tree-node';
import { isClassicLayer } from 'cadenza/workbook/map/workbook-layer-type-util';
import type { MapCouplingGroupId } from 'cadenza/workbook/model/map-coupling-group-dto';
import { MapViewSettingsKey } from 'cadenza/workbook/model/map-view-settings-key.gen';
import type { OverviewMapContext } from 'cadenza/workbook/state/workbook-map-view.state';
import { type MapViewMetadata } from 'cadenza/workbook/state/workbook-map-view.state';
import { is3DMapView } from 'cadenza/workbook/view/3d/3d-view-utils';
import {
  getActiveWorksheetViews,
  getCouplingLayerLimit,
  getDrillThroughTargets,
  getEmbeddingTargets,
  getViewById,
  getViewMetadata,
  getViews,
  isIntegrated,
} from 'cadenza/workbook/workbook-core-selectors.state';
import { slice } from 'cadenza/workbook/workbook-core-slice.state';
import { getSetting, getSettingOrSettingDefault } from 'cadenza/workbook/workbook-settings-util';
import type { ViewWithMetadata, WorkbookState } from 'cadenza/workbook/workbook.state';
import { colStart, rowStart } from 'cadenza/workbook/worksheet/dto/grid-area-dto';

const VECTOR_TILES_SRS_CODE = 'EPSG:3857' as Authority;

export function getMapCouplingGroupWithStateById(
  state: RootStateOr<WorkbookState>,
  couplingGroupId: MapCouplingGroupId,
) {
  return slice(state).mapCouplingGroupsById[couplingGroupId];
}

export function getMapCouplingGroupWithState(state: RootStateOr<WorkbookState>, viewId: ViewId) {
  // ?. because view might already be removed from state when executing this in an observer
  const groupId = getViewById(state, viewId)?.mapViewContext?.mapCouplingGroupId;
  return groupId ? getMapCouplingGroupWithStateById(state, groupId) : undefined;
}

export function getMapViewContext(state: RootStateOr<WorkbookState>, viewId: ViewId) {
  const view = getViewById(state, viewId);
  assert(view.type === 'MAP', 'Map context is available only for map views.');
  assertNonNullable(view.mapViewContext);
  return view.mapViewContext;
}

export function getMostLeftTopViewOfGroup(state: RootStateOr<WorkbookState>, groupId: MapCouplingGroupId) {
  const views = getViewsOfMapCouplingGroup(state, groupId);
  return getMostLeftTopView(views);
}

export function getViewsOfMapCouplingGroup(state: RootStateOr<WorkbookState>, groupId: MapCouplingGroupId) {
  return getViews(state).filter((view) => view.mapViewContext?.mapCouplingGroupId === groupId);
}

function getMostLeftTopView(views: ViewDto[]) {
  return views.reduce((currentMostLeftTopView, candidateView) => {
    // Only non-integrated views (that have a layout) are considered here, because integrated views cannot be part of coupling group
    const currentMostLeftTopViewsGridArea = requireNonNullable(currentMostLeftTopView.layout);
    const candidateViewsGridArea = requireNonNullable(candidateView.layout);

    const isMoreLeft = colStart(candidateViewsGridArea) < colStart(currentMostLeftTopViewsGridArea);
    const isEqualLeft = colStart(candidateViewsGridArea) === colStart(currentMostLeftTopViewsGridArea);
    const isMoreTop = rowStart(candidateViewsGridArea) < rowStart(currentMostLeftTopViewsGridArea);

    return isMoreLeft || (isEqualLeft && isMoreTop) ? candidateView : currentMostLeftTopView;
  });
}

export function getZoomRelatedViewIds(state: RootStateOr<WorkbookState>) {
  return slice(state).zoomRelatedViewIds ?? [];
}

export const getMapViewSrs = (state: RootStateOr<WorkbookState>, viewId: ViewId) =>
  getMapViewContext(state, viewId).projection.code;

export const mapViewSupportsVectorTiles = (state: RootStateOr<WorkbookState>, viewId: ViewId) =>
  getMapViewSrs(state, viewId) === VECTOR_TILES_SRS_CODE;

export const getMapViewMinAllowedScale = (state: RootStateOr<WorkbookState>, viewId: ViewId) =>
  getMapViewContext(state, viewId).minAllowedScale;
export const getMapViewMaxAllowedScale = (state: RootStateOr<WorkbookState>, viewId: ViewId) =>
  getMapViewContext(state, viewId).maxAllowedScale;
/** Returns the overview map (only if it's integrated) for a potential main map, otherwise undefined **/
export const getIntegratedOverviewMap = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  const overviewMap = getOverviewMap(state, viewId);
  if (overviewMap && isIntegrated(overviewMap)) {
    return overviewMap;
  }
};
/** Returns the id of overview map (only if it's integrated) for a potential main map, otherwise undefined **/
export const getIntegratedOverviewMapId = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  const overviewMap = getOverviewMap(state, viewId);
  if (overviewMap && isIntegrated(overviewMap)) {
    return overviewMap.id;
  }
};
export const hasIntegratedOverviewMap = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  return !!getIntegratedOverviewMap(state, viewId);
};
/** Returns the id of the main map for a potential overview map, otherwise undefined **/
export const getOverviewMainMapViewId = (state: RootStateOr<WorkbookState>, viewId: ViewId) =>
  getMapViewContext(state, viewId).overviewMainMapViewId;
/** Returns true if the view is the main map for an overview map, otherwise false **/
export const isMainMapView = (state: RootStateOr<WorkbookState>, viewId: ViewId) =>
  Boolean(getOverviewMap(state, viewId));
/** Returns true if the view is an overview map and thus has a reference to its main map **/
export const isOverviewMap = (state: RootStateOr<WorkbookState>, viewId: ViewId) =>
  Boolean(getViewById(state, viewId)?.mapViewContext?.overviewMainMapViewId);
export const isSpatialConditionGeometryVisibleInView = (state: RootStateOr<WorkbookState>, viewId: ViewId) =>
  !isOverviewMap(state, viewId) &&
  getSetting(
    getViewById(state, viewId),
    MapViewSettingsKey.toPersistenceKey('DISPLAY_SPATIAL_FILTER_GEOMETRIES'),
    false,
  );
export const hasLegacyOverviewMap = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  return !!getViewById(state, viewId)?.mapViewContext?.locatorScaleMode;
};
export const getOverviewMainMapMaxExtentByOverviewMapViewId = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  const overviewMainMapViewId = getOverviewMainMapViewId(state, viewId);
  if (overviewMainMapViewId) {
    return getMapViewContext(state, overviewMainMapViewId).maxExtent;
  }
};

export const getOverviewMapContextByMainMapViewId = (
  state: RootStateOr<WorkbookState>,
  viewId?: ViewId,
): OverviewMapContext | undefined => {
  const overviewMapContext = slice(state).overviewMapContextsByMainMapViewId;
  if (overviewMapContext && viewId) {
    return overviewMapContext[viewId];
  }
  return undefined;
};

export const getOverviewMainMapCurrentExtentByOverviewMapViewId = (
  state: RootStateOr<WorkbookState>,
  overviewMapViewId: ViewId,
) => {
  const mainMapViewId = getOverviewMainMapViewId(state, overviewMapViewId);
  return getOverviewMapContextByMainMapViewId(state, mainMapViewId)?.currentExtent;
};

export const getSuitableOverviewMapsForView = (
  state: RootStateOr<WorkbookState>,
  mainMapCandidateId: ViewId,
): ViewWithMetadata[] => {
  const srs = getMapViewSrs(state, mainMapCandidateId);
  return getActiveWorksheetViews(state).filter((view) => {
    if (view.type !== 'MAP' || view.id === mainMapCandidateId) {
      return false; // Not a map or main map itself
    }
    /// Fits the checks in BindOverviewMapViewCommandExecutor.java
    const mapViewContext = getMapViewContext(state, view.id);
    const usesSameSrs = mapViewContext.projection.code === srs;
    const overviewMainMapViewId = mapViewContext.overviewMainMapViewId;
    const isOverviewForOtherView = overviewMainMapViewId && overviewMainMapViewId !== mainMapCandidateId;
    const isMainMapViewForOtherView = isMainMapView(state, view.id);
    const isCoupled = mapViewContext.mapCouplingGroupId != null;
    const hasClassicLayers = Object.values(view.layerDefinitions).some(isClassicLayer);
    const isUsedAsEmbedding = getEmbeddingTargets(state).some(
      ({ viewId: embeddingViewId }) => embeddingViewId === view.id,
    );
    const isUsedAsDrillThrough = getDrillThroughTargets(state)
      .filter((target) => target.type === 'VIEW')
      .some(({ viewId: drillThroughViewId }) => drillThroughViewId === view.id);
    return (
      usesSameSrs &&
      !isOverviewForOtherView &&
      !isCoupled &&
      !hasClassicLayers &&
      !isUsedAsEmbedding &&
      !isUsedAsDrillThrough &&
      !isMainMapViewForOtherView &&
      !is3DMapView(view)
    );
  });
};
export const isSingleMapLayerInVisibleRange = (state: RootStateOr<WorkbookState>, viewId: ViewId, layerId: LayerId) =>
  getViewMetadata<MapViewMetadata>(state, viewId)?.visibleRangeByLayerId?.[layerId] ?? true;
export const isCouplingLayerLimitExceededForGroupByViewId = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  const mapCouplingGroupId = getMapViewContext(state, viewId).mapCouplingGroupId;
  return mapCouplingGroupId && isCouplingLayerLimitExceededForGroup(state, mapCouplingGroupId);
};
export const isCouplingLayerLimitExceededForGroup = (
  state: RootStateOr<WorkbookState>,
  groupId: MapCouplingGroupId,
) => {
  const groupLayerCount = getCouplingLimitRelevantLayerCountOfGroup(state, groupId);
  const couplingLayerLimit = getCouplingLayerLimit(state);
  return groupLayerCount > couplingLayerLimit;
};

/**
 * Returns the count of layers of the given map view, relevant for coupling of map views.
 * If already coupled, it will return the layer count for the group, of only the view otherwise.
 */
export function getCouplingLimitRelevantLayerCountOfViewOrGroup(state: RootStateOr<WorkbookState>, viewId: ViewId) {
  const mapCouplingGroupId = getMapViewContext(state, viewId).mapCouplingGroupId;
  return mapCouplingGroupId ?
      getCouplingLimitRelevantLayerCountOfGroup(state, mapCouplingGroupId)
    : getCouplingLimitRelevantLayerCountOfView(getViewById(state, viewId));
}

export function isMapViewCoupledWithOtherMapView(state: RootStateOr<WorkbookState>, viewId: ViewId) {
  return !!getMapViewContext(state, viewId).mapCouplingGroupId;
}

function getCouplingLimitRelevantLayerCountOfGroup(state: RootStateOr<WorkbookState>, groupId: MapCouplingGroupId) {
  return getViewsOfMapCouplingGroup(state, groupId)
    .map((view) => getCouplingLimitRelevantLayerCountOfView(view))
    .reduce((sum, layerCount) => sum + layerCount, 0);
}

/**
 * 1. Far all layers except group layers in map view the sum is increased by one (independent of layer visibility)
 * 2. For standard group layers the sum is increased by calculating the sum of all nested layers by using rules 1,2 and 3 (independent of layer visibility)
 * 3. For toggle layers the sum is increased by the maximum sum of all nested layers by using rules 1,2,3 (independent of layer visibility)
 *
 * @param view - view, whose layers will be counted
 */
export function getCouplingLimitRelevantLayerCountOfView(view: ViewDto) {
  const layerTree = view.layerTree;
  const layerDefinitions = view.layerDefinitions;
  return couplingLimitRelevantLayerCountOfView(layerTree, layerDefinitions);
}

function couplingLimitRelevantLayerCountOfView(
  layerTree: WorkbookLayerTreeNode[],
  layerDefinitions: Record<LayerId, WorkbookLayerDto>,
  isChildOfToggleGroup: boolean = false,
): number {
  return layerTree.reduce((sum, layer) => {
    if (layerDefinitions[layer.id].structureType === 'GROUP') {
      return sum + couplingLimitRelevantLayerCountOfView(layer.children, layerDefinitions);
    }
    if (layerDefinitions[layer.id].structureType === 'TOGGLE') {
      return (
        sum +
        layer.children.reduce((max, child) => {
          if (child.children.length) {
            const count = couplingLimitRelevantLayerCountOfView(
              child.children,
              layerDefinitions,
              layerDefinitions[child.id].structureType === 'TOGGLE',
            );
            return Math.max(max, count);
          }
          return max;
        }, 1)
      );
    }
    return isChildOfToggleGroup ? Math.max(sum, 1) : sum + 1;
  }, 0);
}

export const getMapViewLayoutSetting = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  const view = getViewById(state, viewId);
  return view ?
      getSettingOrSettingDefault<MapViewLayout>(view, MapViewSettingsKey.toPersistenceKey('LAYOUT'))
    : 'OVERLAY';
};

export const isSidebarMapLayout = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  const layout = getMapViewLayoutSetting(state, viewId);
  return layout === 'SIDEBAR_LEFT' || layout === 'SIDEBAR_RIGHT';
};

export const isOverviewMapEnabled = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  const viewById = getViewById(state, viewId);
  return viewById ? isOverviewMapEnabledForView(viewById) : false;
};

export const isLegendEnabled = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  const viewById = getViewById(state, viewId);
  return viewById ? isLegendEnabledForView(viewById) : false;
};

export const isOverviewMapOrLegendEnabled = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  const viewById = getViewById(state, viewId);
  return viewById ? isLegendEnabledForView(viewById) || isOverviewMapEnabledForView(viewById) : false;
};

function isOverviewMapEnabledForView(view: ViewDto) {
  return getSettingOrSettingDefault<boolean>(view, MapViewSettingsKey.toPersistenceKey('OVERVIEW_MAP_ENABLED'));
}

function isLegendEnabledForView(view: ViewDto) {
  return getSettingOrSettingDefault<boolean>(view, MapViewSettingsKey.toPersistenceKey('LEGEND_ENABLED'));
}

/** Returns the overview map for a potential main map, otherwise undefined **/
export const getOverviewMap = (state: RootStateOr<WorkbookState>, viewId: ViewId) =>
  getActiveWorksheetViews(state).find((view) => view.mapViewContext?.overviewMainMapViewId === viewId);
